import { useAppConfigurationStore } from '@/stores/useAppConfigurationStore'
import { useErrorStore } from '@/stores/errorStore'
import { registerGuard } from '@/router/guards'
import { createRouter, createWebHistory, Router, RouterScrollBehavior } from 'vue-router'
import { initialRoutes } from './initialRountes'
import * as Sentry from "@sentry/vue";
import { safeLazyLoad } from '@/utils/lazyLoad'
import { formatAxiosErrorDetails, isAxiosNetworkError } from '@/utils/axiosErrors'
import { isRouteModuleLoadError } from '@/utils/routeModuleErrors'

const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
    // always scroll to top
    return { left: 0, top: 0 };
};

const router = createRouter({
    history: createWebHistory(),
    routes: initialRoutes,
    scrollBehavior,
})
registerGuard(router)

function addRoute(router: Router, route: any) {
   
    if (router.hasRoute(route.name)) {
        router.removeRoute(route.name);
    }
    if (!router.hasRoute(route.name)) {
        router.addRoute(route);
    }
}




export const dynamicRoutes = (router: Router) => {

    const appConfigurationStore = useAppConfigurationStore()

    let route = {
        path: appConfigurationStore.configuration.paths.home,
        name: 'home',
        meta: { requiresAuth: true }, // Requires auth BUT router guard allows redirect callbacks
        component: () => safeLazyLoad(() => import('../pages/HomePage.vue'))
    };

    addRoute(router, route);


    route = {
        path: appConfigurationStore.configuration.paths.myEvents,
        name: 'my-events',
        meta: { requiresAuth: true },
        component: () => safeLazyLoad(() => import('../pages/events/MyEventsPage.vue'))
    };

    addRoute(router, route);

    // Integration page route
    route = {
        path: '/integration',
        name: 'integration',
        component: () => safeLazyLoad(() => import('../pages/IntegrationPage.vue'))
    };

    addRoute(router, route);

    route = {
        path: '/integration/dwarf',
        name: 'integration-dwarf',
        component: () => safeLazyLoad(() => import('../pages/ApiIntegrationDwarfPage.vue'))
    };

    addRoute(router, route);

    // Unsubscribe routes
    route = {
        path: '/ttt/unsubscribe',
        name: 'unsubscribe-ttt',
        component: () => safeLazyLoad(() => import('../pages/UnsubscribeTidTilTurPage.vue'))
    };

    addRoute(router, route);

}

router.beforeEach((to, from, next) => {

    if (router.hasRoute('my-events')) {
   //     replaceRoute(router);
    }

    next();
});

router.onError((error, to) => {
    if (!isRouteModuleLoadError(error) && !isAxiosNetworkError(error)) {
        return
    }

    const label = isRouteModuleLoadError(error) ? 'Module/route load failed' : 'API network error'
    console.error(`🔴 ${label} on route:`, error.message, 'Route:', to?.fullPath)

    const errorStore = useErrorStore()

    errorStore.sendError({
        show: true,
        message: 'Der opstod et problem med at indlæse siden. Vi prøver igen om få sekunder...'
    })

    if (isAxiosNetworkError(error)) {
        errorStore.sendErrorWithDetails(
            'Der opstod et netværksproblem med at hente data. Prøv igen om et øjeblik.',
            formatAxiosErrorDetails(error),
            to?.name?.toString() ?? 'home',
        )
        return
    }

    if (Sentry && Sentry.captureException) {
        const sentryType = error.message.includes("Couldn't resolve component")
            ? 'component_resolve_failure'
            : 'module_import_failure'

        Sentry.captureException(error, {
            handled: true,
            level: 'error',
            tags: {
                type: sentryType,
                source: 'router.onError',
                route: to?.name?.toString() || 'unknown'
            },
            contexts: {
                router: {
                    to: to?.fullPath,
                    name: to?.name?.toString()
                },
            }
        })
    }

    setTimeout(() => {
        const RELOAD_KEY = 'app_reload_attempted';
        const lastReload = sessionStorage.getItem(RELOAD_KEY);
        const now = Date.now();

        if (lastReload && (now - parseInt(lastReload)) < 10000) {
            console.error('🛑 Multiple reload errors in 10s. Stopping automatic reload.');
            return;
        }

        sessionStorage.setItem(RELOAD_KEY, now.toString());

        if (!to?.fullPath) {
            window.location.reload()
        } else {
            window.location.href = to.fullPath
        }
    }, 2000)
})

export default router
